// Which version of the 3D book is on the customer's screen, and when to swap it.
//
// ─── WHY THIS EXISTS (2026-10-18, order #1248) ──────────────────────────────
//
// /preview/:token swapped the open book only when the ORDER's
// `previews_generated_at` changed. The server render draws the 3D pages itself,
// in the same robot browser, right after the PDF — and that path never stamped
// the order column. #1248 sat at NULL through six regenerations: the banner
// « Vous avez modifié votre album… La nouvelle version arrive ici toute seule »
// went away when the new pages landed, and the OLD pages stayed on screen until
// somebody reloaded. The promise on the banner was not true.
//
// Each album's manifest carries its own `generated_at` (the manifest is written
// LAST, so its date is the moment that album's pages became complete), and
// /preview-link/public already sends it. The book's version is those dates.
//
// ⚠️ AN ALBUM THAT DISAPPEARS IS BEING REWRITTEN, NOT GONE. The server's
// rasterizer wipes the album's folder (manifest included) before uploading the
// new pages, so for about a minute the response lists that album as pending.
// Swapping to that response would pull the book out from under the customer;
// keep showing what they have until the new manifest lands.

export interface BookVersionAlbum {
  album_index: number;
  generated_at?: string | null;
}

export interface BookVersionSource {
  previews_generated_at?: string | null;
  albums: BookVersionAlbum[];
}

/** A stable string naming the book a response describes. Pure. */
export function bookVersionKey(d: BookVersionSource | null | undefined): string {
  if (!d) return 'none';
  const orderAt = d.previews_generated_at ?? '';
  return [...d.albums]
    .sort((a, b) => a.album_index - b.album_index)
    // The order-level stamp only stands in for an album that carries no date of
    // its own — otherwise a server stamp landing seconds after the manifest
    // would swap the same pages a second time.
    .map((a) => `${a.album_index}:${a.generated_at ?? orderAt}`)
    .join(',') || `order:${orderAt}`;
}

/**
 * Should the page replace the book it shows with this response? Pure.
 *
 * Yes on the first load; afterwards only when the response describes a
 * different book AND still holds every album the customer can see.
 */
export function shouldSwapBook(opts: {
  firstLoad: boolean;
  shown: BookVersionSource | null;
  next: BookVersionSource;
}): boolean {
  const { firstLoad, shown, next } = opts;
  if (firstLoad || !shown) return true;
  if (next.albums.length === 0) return false;
  const nextAlbums = new Set(next.albums.map((a) => a.album_index));
  if (shown.albums.some((a) => !nextAlbums.has(a.album_index))) return false;
  return bookVersionKey(next) !== bookVersionKey(shown);
}

/**
 * The index (into `next.albums`) of the album the reader was on. Pure.
 * `albums` is an array of the albums that have pages, so a newly finished
 * album shifts the indexes — follow the album, not the slot.
 */
export function albumIdxAfterSwap(shown: BookVersionSource, next: BookVersionSource, idx: number): number {
  const was = shown.albums[idx]?.album_index;
  const j = was == null ? -1 : next.albums.findIndex((a) => a.album_index === was);
  return j >= 0 ? j : Math.min(Math.max(idx, 0), Math.max(next.albums.length - 1, 0));
}

/** Poll faster while the page says a new version is on its way. */
export const POLL_OUTDATED_MS = 5_000;
export const POLL_WATCH_MS = 12_000;
/** While a quick redraw runs (2026-10-21): it lands in seconds — look often. */
export const POLL_BUILDING_MS = 2_000;

/** The bar's width for the album on screen while its new version is drawn; null = none running. Pure. */
export function buildingPctFor(
  quick: Array<{ album: number; pct: number; state?: string }> | null | undefined,
  albumIndex: number | null,
): number | null {
  if (albumIndex == null) return null;
  const j = (quick ?? []).find((q) => q.album === albumIndex);
  if (!j) return null;
  return Math.max(3, Math.min(98, Math.round(Number(j.pct) || 0)));
}
